import { useIntl } from 'react-intl';
import * as z from 'zod';

import { formSchema } from 'src/features/cashCollections/components/CashCollectionsTable';
import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/utils/messages';

import type { UseFormReturn } from 'react-hook-form';

type EditCashCollectionStatusProps = {
    form: UseFormReturn<z.infer<typeof formSchema>>;
    onSubmit: () => void;
    isModalOpen: boolean;
    onOpenChange: () => void;
};

const EditCashCollectionStatus = ({ form, onSubmit, isModalOpen, onOpenChange }: EditCashCollectionStatusProps) => {
    const { formatMessage } = useIntl();

    const statusTranslationMap = {
        paid: formatMessage(cashCollectionsMessages.cashCollectionsPaid),
        collected: formatMessage(cashCollectionsMessages.cashCollectionsCollected),
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-2">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>{formatMessage(cashCollectionsMessages.editCashCollection)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(cashCollectionsMessages.cashCollectionStatusChange)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(cashCollectionsMessages.cashCollectionStatus)}
                                    </FormLabel>
                                    <FormControlStack className="mr-3">
                                        <FormControl>
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue
                                                        placeholder={formatMessage(generalMessages.selectValue)}
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.entries(statusTranslationMap).map(([key, value]) => (
                                                        <SelectItem key={key} value={key}>
                                                            {value}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage>{form.formState.errors.status && 'foo'}</FormMessage>
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button onClick={form.handleSubmit(onSubmit)} type="submit" className="flex-1">
                        {formatMessage(cashCollectionsMessages.editCashCollection)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditCashCollectionStatus;
